import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { Suggestion } from 'Types/prompt';

import LoadingMask from '../../../components/LoadingMask';
import { useDrawer } from '../../../components/Providers/DrawerProvider';
import { useFetchData } from '../../../hooks/useFetchData';
import { useStepObserver } from '../../../hooks/useStepObserver';
import { useTestId } from '../../../hooks/useTestId';
import { PromptSuggestionStep } from '../../../Types/enums';
import { promptSuggestionsPathUrl } from '../../../util/routesUrls';
import StepBuilderInputSection from './StepBuilderInputSection';
import StepConnectorIcon from './StepConnectorIcon';

interface PromptStepBuilderProps {
  onComplete: (text: string) => void;
  onClose: () => void;
}

const CustomStepConnector = styled(StepConnector)(() => ({
  [`& .MuiStepConnector-line`]: { borderColor: '#D0D5DD' },
  [`&.Mui-completed .MuiStepConnector-line`]: { borderColor: '#50658E' },
  [`&.Mui-active .MuiStepConnector-line`]: { borderColor: '#50658E' },
}));

const StepBuilderHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '15px',
  paddingRight: '15px',
  paddingTop: '10px',
  paddingBottom: '5px',
  justifyContent: 'space-between',
}));

const StepContentForm = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'row',
  height: 'calc(100dvh - 140px)',
  gap: '5px',
  width: '100%',
}));

const StepContentColumnContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  paddingLeft: '25px',
  paddingRight: '25px',
  gap: '10px',
}));

const PreviewBox = styled(Box)(() => ({
  flex: 1,
  border: '1px solid #0047FF',
  borderRadius: '10px',
  padding: '10px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  textAlign: 'left',
  backgroundColor: '#F5F5F5',
  pointerEvents: 'none',
}));

const ButtonBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '20px',
  paddingTop: '10px',
}));

const PromptStepBuilder: React.FC<PromptStepBuilderProps> = ({ onComplete, onClose }) => {
  const { closeDrawer } = useDrawer();
  const tid = useTestId('prompt-step-builder');
  const { data: suggestions, loading } = useFetchData<Suggestion[]>({
    urlPath: promptSuggestionsPathUrl(),
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [activeStep, setActiveStep] = useState(0);
  const [promptStepValues, setPromptStepValues] = useState<string[]>([]);
  const promptText = promptStepValues.filter(Boolean).join('<br>');

  const inputText = useMemo(
    () => [
      {
        label: 'Define Role',
        step: PromptSuggestionStep.ROLE,
        suggestions: suggestions?.filter((s) => s.step === PromptSuggestionStep.ROLE),
      },
      {
        label: 'Describe Purpose / Goal',
        step: PromptSuggestionStep.GOAL,
        suggestions: suggestions?.filter((s) => s.step === PromptSuggestionStep.GOAL),
      },
      {
        label: 'Prompt Knowledge',
        step: PromptSuggestionStep.KNOWLEDGE,
        suggestions: suggestions?.filter((s) => s.step === PromptSuggestionStep.KNOWLEDGE),
      },
      {
        label: 'Choose Output Format',
        step: PromptSuggestionStep.OUTPUT,
        suggestions: suggestions?.filter((s) => s.step === PromptSuggestionStep.OUTPUT),
      },
      {
        label: 'Add Tone / Instruction / Rules',
        step: PromptSuggestionStep.TONE,
        suggestions: suggestions?.filter((s) => s.step === PromptSuggestionStep.TONE),
      },
    ],
    [suggestions]
  );

  const handlePromptValuesUpdate = useCallback((index: number, text: string) => {
    setPromptStepValues((prev) => {
      const updated = [...prev];
      updated[index] = text;
      return updated;
    });
  }, []);

  const handleStepClick = useCallback((index: number) => {
    sectionRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const onSubmit = useCallback(() => {
    if (promptText !== undefined && promptText.trim() !== '') {
      onComplete(promptText);
    }
  }, [promptText, onComplete]);

  useStepObserver(containerRef, sectionRefs, setActiveStep);

  return (
    <>
      <LoadingMask isLoading={loading} />
      <Box>
        <StepBuilderHeader>
          <Typography variant="h5">Step by Step Prompt</Typography>
          <IconButton onClick={closeDrawer} data-testid={tid('close')}>
            <Close />
          </IconButton>
        </StepBuilderHeader>

        <Stepper activeStep={activeStep} alternativeLabel connector={<CustomStepConnector />}>
          {inputText.map((item, index) => (
            <Step key={item.label} completed={index < activeStep}>
              <StepLabel
                StepIconComponent={StepConnectorIcon}
                onClick={() => handleStepClick(index)}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '12px',
                  },
                }}
                data-testid={tid('label', item.step)}
              >
                {item.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        <Form
          onSubmit={onSubmit}
          render={({ handleSubmit }) => (
            <StepContentForm onSubmit={handleSubmit}>
              <StepContentColumnContainer ref={containerRef} sx={{ flex: 1.2, mt: 2 }}>
                {inputText.map((item, idx) => (
                  <StepBuilderInputSection
                    key={idx}
                    idx={idx + 1}
                    stepType={item.step}
                    stepLabel={`${idx + 1}. ${item.label}`}
                    sectionRef={(el) => {
                      sectionRefs.current[idx] = el;
                    }}
                    suggestions={item.suggestions}
                    onChange={(text) => handlePromptValuesUpdate(idx, text)}
                  />
                ))}
              </StepContentColumnContainer>

              <StepContentColumnContainer sx={{ flex: 1, mt: 2 }}>
                <Typography variant="h6" color="#0047FF" sx={{}}>
                  Preview
                </Typography>
                <PreviewBox data-testid={tid('preview-box')}>
                  <Typography
                    color="text.secondary"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: promptText }}
                  />
                </PreviewBox>
                <ButtonBox>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={onClose}
                    data-testid={tid('back')}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    data-testid={tid('continue')}
                  >
                    Continue
                  </Button>
                </ButtonBox>
              </StepContentColumnContainer>
            </StepContentForm>
          )}
        />
      </Box>
    </>
  );
};

export default PromptStepBuilder;
